<script setup>
import { ref, computed } from 'vue';
import {
  WORKFLOW_FLOW_VARIABLE_GROUPS,
  WORKFLOW_VARIABLE_DRAG_TYPE,
} from './workflowFlowVariableCatalog';
import {
  resolveWorkflowToken,
  formatPinnedSampleValue,
} from './workflowEditorPinnedExecution';

const props = defineProps({
  pinnedExecution: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['collapse', 'clearPinnedExecution']);

const bindingsForSamples = computed(() => {
  const b = props.pinnedExecution?.bindings;
  if (b && typeof b === 'object' && !Array.isArray(b)) return b;
  return null;
});

const showPinnedBanner = computed(
  () =>
    bindingsForSamples.value &&
    Object.keys(bindingsForSamples.value).length > 0 &&
    props.pinnedExecution?.executionId != null
);

const searchQuery = ref('');

const normalizedQuery = computed(() => searchQuery.value.trim().toLowerCase());

const sampleLine = token => {
  if (!bindingsForSamples.value) return '';
  const raw = resolveWorkflowToken(bindingsForSamples.value, token);
  if (raw === null || raw === undefined) return '';
  return formatPinnedSampleValue(raw);
};

const filteredGroups = computed(() => {
  const q = normalizedQuery.value;
  if (!q) return WORKFLOW_FLOW_VARIABLE_GROUPS;

  return WORKFLOW_FLOW_VARIABLE_GROUPS.map(group => {
    const gl = group.label.toLowerCase();
    const gh = (group.hint || '').toLowerCase();
    const vars = group.variables.filter(v => {
      const tl = v.label.toLowerCase();
      const tt = v.token.toLowerCase();
      const sample = sampleLine(v.token).toLowerCase();
      return (
        tl.includes(q) ||
        tt.includes(q) ||
        gl.includes(q) ||
        gh.includes(q) ||
        (sample && sample.includes(q))
      );
    });
    if (!vars.length) return null;
    return { ...group, variables: vars };
  }).filter(Boolean);
});

const onVarDragStart = (event, token) => {
  event.dataTransfer.setData(WORKFLOW_VARIABLE_DRAG_TYPE, token);
  event.dataTransfer.setData('text/plain', token);
  event.dataTransfer.effectAllowed = 'copy';
};
</script>

<template>
  <aside
    class="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden bg-n-solid-2 text-n-slate-12"
  >
    <div class="shrink-0 border-b border-n-weak px-3 py-3">
      <div class="flex items-start justify-between gap-2">
        <h2 class="min-w-0 flex-1 text-sm font-semibold leading-snug text-n-slate-12">
          Dados entre nós
        </h2>
        <button
          type="button"
          class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-n-slate-11 transition-colors duration-200 ease-out hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9 motion-reduce:transition-none"
          title="Ocultar painel de variáveis"
          @click="emit('collapse')"
        >
          <span
            class="i-lucide-chevrons-left size-4 shrink-0 rotate-180"
            aria-hidden="true"
          />
        </button>
      </div>
      <p class="mt-1 text-[11px] leading-snug text-n-slate-11">
        Arraste uma variável para um campo de texto no painel ao lado para inserir o texto no cursor.
      </p>
      <div
        v-if="showPinnedBanner"
        class="mt-3 rounded-lg border border-n-teal-7/35 bg-n-teal-3/25 px-2.5 py-2"
      >
        <div class="flex items-start gap-2">
          <p class="min-w-0 flex-1 text-[11px] leading-snug text-n-slate-12">
            <span class="font-medium">Referência da execução</span>
            #{{ pinnedExecution.executionId }}
            <span class="text-n-slate-11">
              · valores em cada variável vêm desta cópia (apenas no editor).
            </span>
          </p>
          <button
            type="button"
            class="inline-flex shrink-0 rounded-md px-2 py-1 text-[10px] font-medium text-n-teal-11 transition-colors hover:bg-n-alpha-2 hover:text-n-teal-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
            @click="emit('clearPinnedExecution')"
          >
            Limpar
          </button>
        </div>
      </div>
      <label class="sr-only" for="flow-variables-search">
        Pesquisar variáveis
      </label>
      <input
        id="flow-variables-search"
        v-model="searchQuery"
        type="search"
        autocomplete="off"
        class="reset-base mt-3 box-border w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
        placeholder="Pesquisar variáveis…"
      >
    </div>

    <div
      class="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden overscroll-contain px-2 py-2 [scrollbar-width:thin] [-ms-overflow-style:auto] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-n-slate-8/40 [&::-webkit-scrollbar-track]:bg-transparent"
    >
      <template v-if="filteredGroups.length">
        <div
          v-for="group in filteredGroups"
          :key="group.id"
          class="rounded-lg border border-n-weak bg-n-solid-1"
        >
          <div
            class="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          >
            {{ group.label }}
          </div>
          <p
            v-if="group.hint"
            class="border-t border-n-weak px-2 pb-2 pt-1.5 text-[10px] leading-snug text-n-slate-11"
          >
            {{ group.hint }}
          </p>
          <ul class="list-none border-t border-n-weak px-1.5 pb-2 pt-1">
            <li
              v-for="(v, vi) in group.variables"
              :key="`${group.id}-${vi}-${v.token}`"
              class="mt-1 first:mt-0"
            >
              <div
                draggable="true"
                class="cursor-grab rounded-md px-1.5 py-1.5 transition-colors hover:bg-n-alpha-2 active:cursor-grabbing"
                @dragstart="onVarDragStart($event, v.token)"
              >
                <div class="min-w-0">
                  <p class="text-[11px] font-medium leading-snug text-n-slate-12">
                    {{ v.label }}
                  </p>
                  <p
                    class="mt-0.5 truncate font-mono text-[10px] leading-snug text-n-teal-11"
                    :title="v.token"
                  >
                    {{ v.token }}
                  </p>
                  <div
                    v-if="sampleLine(v.token)"
                    class="mt-1.5 max-w-full"
                  >
                    <span
                      class="inline-block max-w-full rounded-md border border-n-teal-8/50 bg-n-teal-4/45 px-2 py-1 font-mono text-[10px] leading-snug text-n-teal-12 shadow-sm line-clamp-3 whitespace-pre-wrap break-words"
                      :title="sampleLine(v.token)"
                    >
                      {{ sampleLine(v.token) }}
                    </span>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </template>
      <p
        v-else
        class="px-2 py-4 text-center text-xs leading-snug text-n-slate-11"
      >
        Nenhuma variável corresponde à pesquisa.
      </p>
    </div>
  </aside>
</template>
